import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, AlertCircle } from "lucide-react";

interface SubscriptionRequiredProps {
  message?: string;
  returnPath?: string;
}

const SubscriptionRequired: React.FC<SubscriptionRequiredProps> = ({
  message = "Access to this feature requires an active subscription.",
  returnPath,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = returnPath || location.pathname;

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
      <div className="w-full max-w-md bg-white rounded-xl shadow-soft p-8">
        <div className="flex justify-center mb-4">
          <div className="bg-indigo-100 rounded-full p-3">
            <AlertCircle size={30} className="text-indigo-600" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-800 mb-3">
          Subscription Required
        </h1>
        <p className="text-gray-600 mb-6">{message}</p>

        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-6 text-left">
          <h2 className="text-lg font-medium text-indigo-800 mb-2">
            Benefits include:
          </h2>
          <ul className="space-y-2">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Access to all pet management features
              </span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Advanced health tracking and reminders
              </span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">Unlimited document storage</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">
                Priority support and future premium features
              </span>
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
          <Button
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            onClick={() => navigate("/pricing", { state: { from } })}
          >
            View Plans
          </Button>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/")}
          >
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;
